// src/hooks/useAuth.tsx

import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { api } from '../services/api';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (_email: string, _password: string) => Promise<void>;
  register: (_email: string, _password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      refreshUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const refreshUser = async () => {
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
    } catch (error) {
      // Error intentionally ignored - we just remove token and set user to null
      console.warn('Failed to refresh user:', error);
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const login = async (_email: string, _password: string) => {
    const response = await api.login(_email, _password);
    localStorage.setItem('token', response.token);
    await refreshUser();
  };

  const register = async (_email: string, _password: string) => {
    const response = await api.register(_email, _password);
    localStorage.setItem('token', response.token);
    await refreshUser();
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      // Error intentionally ignored during logout
      console.warn('Failed to logout:', error);
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
